import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { QuizMode } from '@/types';
import { Button, Card } from '@/components/common';
import { FlagImage } from '@/components/flag';
import { MultipleChoice, TypeAnswer, QuizSummary } from '@/components/quiz';
import { useQuiz } from '@/hooks';
import { useQuizProgressStore } from '@/store';

export function QuizPage() {
  const [searchParams] = useSearchParams();

  const mode = (searchParams.get('mode') as QuizMode) || 'flag-to-name-mc';
  const count = parseInt(searchParams.get('count') || '10', 10);

  const {
    session,
    currentQuestion,
    lastAnswer,
    correctCount,
    startQuiz,
    submitAnswer,
    nextQuestion,
    isComplete,
  } = useQuiz({ mode, questionCount: count });

  const [showResult, setShowResult] = useState(false);
  const [answered, setAnswered] = useState(false);
  const { setProgress, clear: clearProgress } = useQuizProgressStore();

  // Start quiz on mount only
  useEffect(() => {
    startQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update progress in store for nav display
  useEffect(() => {
    if (session && !isComplete) {
      setProgress(session.currentIndex + 1, session.questions.length, correctCount);
    }
  }, [session, isComplete, correctCount, setProgress]);

  // Clear progress on unmount or completion
  useEffect(() => {
    if (isComplete) {
      clearProgress();
    }
    return () => clearProgress();
  }, [isComplete, clearProgress]);

  const handleAnswer = useCallback(
    (code: string) => {
      if (answered) return;
      setAnswered(true);
      submitAnswer(code);
      setShowResult(true);
    },
    [answered, submitAnswer]
  );

  const handleNext = useCallback(() => {
    nextQuestion();
    setShowResult(false);
    setAnswered(false);
  }, [nextQuestion]);

  const handleRestart = useCallback(() => {
    setShowResult(false);
    setAnswered(false);
    startQuiz();
  }, [startQuiz]);

  if (!session) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isComplete) {
    return <QuizSummary session={session} onRestart={handleRestart} />;
  }

  if (!currentQuestion) return null;

  const isTypeMode = mode === 'flag-to-name-type';
  const showFlag = mode.startsWith('flag-to');
  const showFlagOptions = mode === 'name-to-flag-mc';

  return (
    <div className="space-y-6">
      {/* Question */}
      <Card className="text-center space-y-6">
        {showFlag && (
          <div className="flex justify-center">
            <FlagImage
              countryCode={currentQuestion.targetCountry.code}
              countryName={currentQuestion.targetCountry.name}
              size="xl"
              loading="eager"
            />
          </div>
        )}

        {showFlagOptions && (
          <h2 className="text-2xl font-bold">
            {currentQuestion.targetCountry.name}
          </h2>
        )}

        <p className="text-gray-500">
          {showFlag ? 'Which country is this?' : 'Select the correct flag'}
        </p>
      </Card>

      {/* Answer options */}
      {isTypeMode ? (
        <TypeAnswer
          key={currentQuestion.id}
          onSubmit={handleAnswer}
          disabled={answered}
          correctCountry={currentQuestion.targetCountry}
          showResult={showResult}
          isCorrect={lastAnswer?.correct}
        />
      ) : (
        <MultipleChoice
          key={currentQuestion.id}
          options={currentQuestion.options}
          correctCode={currentQuestion.targetCountry.code}
          onSelect={handleAnswer}
          disabled={answered}
          showFlags={showFlagOptions}
          showResult={showResult}
        />
      )}

      {/* Next button */}
      {showResult && (
        <Button onClick={handleNext} className="w-full flex items-center justify-center gap-2">
          Next Question
          <ArrowRight className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}
